// Coverage assessment logic. No "plan", no "modules": we assess which
// representative each customer needs and tailor the language to their scenario.

const REP_META = {
  eu_rep: {
    name: "EU Representative",
    art: "GDPR Article 27",
    region: "the EU",
    entity: "instant_eu",
    benefits: [
      "Your named point of contact for EU regulators and individuals",
      "We handle access requests, erasure requests and data breach queries",
      "Written Agreement available on day one as evidence for auditors",
      "A free initial Article 27 compliance audit",
    ],
  },
  uk_rep: {
    name: "UK Representative",
    art: "UK GDPR Article 27",
    region: "the UK",
    entity: "gdprlocal_uk",
    benefits: [
      "Your named point of contact for the ICO and UK individuals",
      "We handle access requests, erasure requests and data breach queries",
      "Written Agreement available on day one as evidence for auditors",
      "A free initial Article 27 compliance audit",
    ],
  },
  swiss_rep: {
    name: "Swiss Representative",
    art: "FADP Article 14",
    region: "Switzerland",
    entity: "elite_med",
    benefits: [
      "Compliance support from our office in Switzerland",
      "Written Agreement as required under the revised FADP",
      "We liaise with the Swiss regulator on your behalf",
    ],
  },
  eu_ai_rep: {
    name: "EU AI Act Representative",
    art: "AI Act Article 22",
    region: "the EU",
    entity: "instant_eu",
    benefits: [
      "Your authorised representative for EU AI Act obligations",
      "Support evidencing your risk classification and governance",
      "A single point of contact as the AI Act is enforced",
    ],
  },
};

function repStatus({ inScope, outside, covered }) {
  if (!inScope) return "not_needed";
  if (!outside) return "not_needed";
  if (covered) return "covered";
  return "required";
}

export function computeCoverage(a) {
  const outside = !!a.established_outside;
  const items = [];

  const eu = repStatus({ inScope: a.eu_residents, outside, covered: a.has_eu_rep });
  const uk = repStatus({ inScope: a.uk_residents, outside, covered: a.has_uk_rep });
  const ch = repStatus({ inScope: a.swiss_residents, outside, covered: a.has_swiss_rep });
  const ai = a.uses_ai && a.eu_residents ? "recommended" : "not_needed";

  const reason = {
    eu_rep: {
      required: "You handle personal data of EU residents from outside the EU, so GDPR Article 27 requires a named EU representative.",
      covered: "You already list an EU representative, or you are established in the EU, so this is handled.",
      not_needed: a.eu_residents
        ? "You appear to be established inside the EU, so a separate EU representative is not required."
        : "You do not appear to process EU resident data.",
    },
    uk_rep: {
      required: "You handle personal data of UK residents from outside the UK, so the UK GDPR requires a UK representative.",
      covered: "You already list a UK representative, or you are established in the UK, so this is handled.",
      not_needed: a.uk_residents
        ? "You appear to be established inside the UK, so a separate UK representative is not required."
        : "You do not appear to process UK resident data.",
    },
    swiss_rep: {
      required: "You handle data of Swiss residents from outside Switzerland, which triggers a FADP Article 14 representative.",
      covered: "You already list a Swiss representative, so this is handled.",
      not_needed: a.swiss_residents
        ? "You appear to be established inside Switzerland, so a separate representative is not required."
        : "You do not appear to process Swiss resident data.",
    },
    eu_ai_rep: {
      recommended: "You use AI or automated decisioning affecting people in the EU, so an EU AI Act representative is increasingly relevant.",
      not_needed: "You have not indicated AI or automated decisioning affecting EU users.",
    },
  };

  const mk = (id, status) => ({
    id,
    ...REP_META[id],
    status,
    reason: (reason[id] && reason[id][status]) || "",
  });

  items.push(mk("eu_rep", eu));
  items.push(mk("uk_rep", uk));
  items.push(mk("swiss_rep", ch));
  items.push(mk("eu_ai_rep", ai));
  return items;
}

export function actionableIds(coverage) {
  return coverage.filter((c) => c.status === "required" || c.status === "recommended").map((c) => c.id);
}

export function coverageCost(coverage, config, band, currency, currencies) {
  const { fmtMoney } = require("./pricing");
  let total = 0;
  actionableIds(coverage).forEach((id) => {
    const m = config.modules.find((x) => x.id === id);
    if (m && m.priceModel === "monthly") total += m.bands[band] ?? m.bands.SME;
  });
  return { total, display: fmtMoney(total, currency, currencies) };
}

export function scenarioSummary(a, company) {
  const size = { Micro: "small", SME: "mid-sized", Growth: "scaling", Corporate: "large" }[a.scale] || "growing";
  const sector = String(a.sector || "").replace(" / ", " and ").toLowerCase();
  const geos = [a.eu_residents && "EU", a.uk_residents && "UK", a.swiss_residents && "Swiss"].filter(Boolean).join(", ");
  const est = a.established_outside ? "based outside the EU and UK" : "with an EU or UK establishment";
  const who = company && company !== "your company" ? company : "You";
  const verb = who === "You" ? "look like" : "looks like";
  return `${who} ${verb} a ${size} ${sector} business ${est}, handling ${geos || "personal"} resident data. Here is exactly what that means for your representative coverage.`;
}
